/**
 * The analytics read model (ADR-025 decision 2): GET /api/analytics/portfolio.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The contractor dossiers live in `portfolio.ts` beside the registers they serve;
 * this module is the portfolio rollup alone. It parses exactly the shape the contract
 * publishes and decides nothing about scope: the model enforced the caller's predicate
 * inside itself, so what arrives is what this principal may see, and the dashboard can
 * only render it.
 */

import { apiJson, num, str } from "./client";

export interface StateCount {
  /** The canonical vocabulary token, or null for the unattributed group. */
  readonly value: string | null;
  readonly count: number;
}

export interface MonthlyBucket {
  /** The month key, YYYY-MM, zero-filled by the model. */
  readonly month: string;
  readonly count: number;
}

export interface ContractorOpenItems {
  readonly contractorPartyId: string;
  readonly contractorPartyName: string;
  readonly openFindings: number;
  readonly openActions: number;
}

export interface DisciplineRow {
  readonly disciplineId: string;
  readonly inspections: number;
}

export interface PortfolioAnalytics {
  readonly itpStates: readonly StateCount[];
  readonly scheduleStates: readonly StateCount[];
  readonly verificationStates: readonly StateCount[];
  readonly findingStates: readonly StateCount[];
  readonly findingClasses: readonly StateCount[];
  readonly actionTypes: readonly StateCount[];
  readonly rootCauseDistribution: readonly StateCount[];
  readonly monthly: {
    readonly findingsRaised: readonly MonthlyBucket[];
    readonly findingsClosed: readonly MonthlyBucket[];
    readonly verificationsConducted: readonly MonthlyBucket[];
  };
  readonly inspectionsByDiscipline: {
    readonly disciplines: readonly DisciplineRow[];
    readonly unattributed: number;
  };
  readonly contractorOpenItems: readonly ContractorOpenItems[];
}

function stateCounts(value: unknown): StateCount[] {
  if (!Array.isArray(value)) {
    throw new Error("Expected a state count register");
  }
  return value.map((entry) => {
    if (typeof entry !== "object" || entry === null) {
      throw new Error("Expected a state count row");
    }
    const record = entry as Record<string, unknown>;
    return {
      value: record.value === null ? null : str(record.value),
      count: num(record.count),
    };
  });
}

function buckets(value: unknown): MonthlyBucket[] {
  if (!Array.isArray(value)) {
    throw new Error("Expected a monthly bucket register");
  }
  return value.map((entry) => {
    if (typeof entry !== "object" || entry === null) {
      throw new Error("Expected a monthly bucket");
    }
    const record = entry as Record<string, unknown>;
    return { month: str(record.month), count: num(record.count) };
  });
}

function openItems(value: unknown): ContractorOpenItems[] {
  if (!Array.isArray(value)) {
    throw new Error("Expected the contractor open items");
  }
  return value.map((entry) => {
    if (typeof entry !== "object" || entry === null) {
      throw new Error("Expected an open items row");
    }
    const record = entry as Record<string, unknown>;
    return {
      contractorPartyId: str(record.contractor_party_id),
      contractorPartyName: str(record.contractor_party_name),
      openFindings: num(record.open_findings),
      openActions: num(record.open_actions),
    };
  });
}

export async function getPortfolioAnalytics(
  token: string,
  signal?: AbortSignal,
): Promise<PortfolioAnalytics> {
  return apiJson(
    token,
    "/analytics/portfolio",
    (raw) => {
      if (typeof raw !== "object" || raw === null) {
        throw new Error("Expected the portfolio analytics body");
      }
      const body = raw as Record<string, unknown>;
      if (typeof body.monthly !== "object" || body.monthly === null) {
        throw new Error("Expected the monthly buckets");
      }
      const monthly = body.monthly as Record<string, unknown>;
      if (
        typeof body.inspections_by_discipline !== "object" ||
        body.inspections_by_discipline === null
      ) {
        throw new Error("Expected the discipline inspection counts");
      }
      const discipline = body.inspections_by_discipline as Record<string, unknown>;
      const disciplineRows = Array.isArray(discipline.disciplines) ? discipline.disciplines : [];
      return {
        itpStates: stateCounts(body.itp_states),
        scheduleStates: stateCounts(body.schedule_states),
        verificationStates: stateCounts(body.verification_states),
        findingStates: stateCounts(body.finding_states),
        findingClasses: stateCounts(body.finding_classes),
        actionTypes: stateCounts(body.action_types),
        rootCauseDistribution: stateCounts(body.root_cause_distribution),
        monthly: {
          findingsRaised: buckets(monthly.findings_raised),
          findingsClosed: buckets(monthly.findings_closed),
          verificationsConducted: buckets(monthly.verifications_conducted),
        },
        inspectionsByDiscipline: {
          disciplines: disciplineRows.map((entry) => {
            if (typeof entry !== "object" || entry === null) {
              throw new Error("Expected a discipline count row");
            }
            const record = entry as Record<string, unknown>;
            return {
              disciplineId: str(record.discipline_id),
              inspections: num(record.inspections),
            };
          }),
          unattributed: num(discipline.unattributed ?? 0),
        },
        contractorOpenItems: openItems(body.contractor_open_items),
      };
    },
    { signal },
  );
}

/** The count of one canonical token in a state register; a token never sent counts zero. */
export function countOf(register: readonly StateCount[], token: string): number {
  return register.filter((row) => row.value === token).reduce((sum, row) => sum + row.count, 0);
}

/** The total of a state register, skipping the unattributed null group only on request. */
export function totalOf(register: readonly StateCount[]): number {
  return register.reduce((sum, row) => sum + row.count, 0);
}
